import {css, html, LitElement, unsafeCSS} from "lit";
import {customElement, state} from "lit/decorators.js";
import {repeat} from "lit/directives/repeat.js";
import {styleMap} from "lit/directives/style-map.js";

type Candle = { id: number, open: number, close: number, high: number, low: number }

const CANDLE_COUNT = 9;
const CANDLE_WIDTH = 13;
const CANDLE_GAP = 7;
const WICK_WIDTH = 2;
const MAX_HEIGHT = 100;
const TRACK_WIDTH = (CANDLE_WIDTH + CANDLE_GAP) * CANDLE_COUNT;

const UPDATE_INTERVAL = 70;
const TICKS_PER_CANDLE = 5;
const MAX_TICK = 13;
const MIN_TICK = 7;

/** Oldest candle's opacity. Anything lower disappears against the backdrop. */
const TRAIL_OPACITY = 0.3;

/** Fraction of the track from either edge where the walk is nudged back toward the middle. */
const REVERSAL_ZONE = 0.35;

/** Must stay in sync with the :host opacity transition below. */
const FADE_OUT_MS = 450;

let nextCandleId = 0;

const clampToTrack = (value: number) => Math.max(Math.min(value, MAX_HEIGHT), 0);

/** Normalizes a candle so its wick always spans at least its body. */
function withConsistentWick(candle: Candle): Candle {
    return {
        ...candle,
        high: Math.max(candle.high, candle.open, candle.close),
        low: Math.min(candle.low, candle.open, candle.close),
    };
}

/** Opens a flat candle at the previous candle's close, so the series stays continuous. */
function openCandle(prev?: Candle): Candle {
    const open = prev ? prev.close : Math.ceil(Math.random() * MAX_HEIGHT);
    return {id: nextCandleId++, open, close: open, high: open, low: open};
}

/** Builds an already-closed candle, used to fill the track before the first tick. */
function seedCandle(prev?: Candle): Candle {
    const candle = openCandle(prev);
    const bodySize = Math.ceil(Math.random() * (MAX_HEIGHT * 0.4));
    const wickSize = Math.ceil(Math.random() * 3);

    if (bodySize > MAX_HEIGHT * 0.2) {
        candle.close = Math.min(candle.open + bodySize, MAX_HEIGHT);
        candle.high = clampToTrack(candle.close + wickSize);
    } else {
        candle.close = Math.abs(candle.open - bodySize);
        candle.low = clampToTrack(candle.close - wickSize);
    }

    return withConsistentWick(candle);
}

/**
 * Walks the open candle one tick. `trend` biases the direction so the series
 * stays on the track instead of pinning to an edge: ±1 is a gentle pull toward
 * the middle, ±2 means an edge is close and the step size drops.
 */
function tickCandle(candle: Candle, trend: number): { candle: Candle, trend: number } {
    const rising = Math.ceil(Math.random() * 10) > (5 + trend);
    const movement = Math.abs(trend) === 2 ? MIN_TICK : MAX_TICK;
    const close = candle.close + (rising ? movement : -movement);

    let nextTrend = close > MAX_HEIGHT / 2 ? -1 : 1;
    if (close > MAX_HEIGHT * (1 - REVERSAL_ZONE)) {
        nextTrend = 2;
    } else if (close < MAX_HEIGHT * REVERSAL_ZONE) {
        nextTrend = -2;
    }

    return {
        candle: withConsistentWick({...candle, close: clampToTrack(close)}),
        trend: nextTrend,
    };
}

function candleColor(candle: Candle): string {
    if (candle.close === candle.open) return 'var(--gc-loading-doji)';
    return candle.close > candle.open ? 'var(--gc-loading-up)' : 'var(--gc-loading-down)';
}

@customElement('gc-loading')
class Loading extends LitElement {

    /**
     * Colors are custom properties rather than literals because this splash paints
     * before auth resolves, so `currentTradingHouse` is not known yet and the element
     * cannot brand itself. A trading house can restyle it by setting these on the host.
     */
    static styles = css`
        :host {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 28px;
            background: var(--gc-loading-bg, #1A1B1E);
            opacity: 1;
            transition: opacity ${unsafeCSS(FADE_OUT_MS)}ms ease;

            --gc-loading-up: #1DC30E;
            --gc-loading-down: #f44336;
            --gc-loading-doji: rgba(255, 255, 255, 0.55);
            --gc-loading-glow: rgba(29, 195, 14, 0.22);
        }

        :host(.is-hidden) {
            opacity: 0;
            pointer-events: none;
        }

        .glow {
            position: absolute;
            width: 560px;
            height: 560px;
            max-width: 90vw;
            max-height: 90vw;
            border-radius: 50%;
            background: radial-gradient(circle, var(--gc-loading-glow) 0%, transparent 70%);
            animation: breathe 3.2s ease-in-out infinite;
        }

        .track {
            position: relative;
            width: ${unsafeCSS(TRACK_WIDTH)}px;
            height: ${unsafeCSS(MAX_HEIGHT)}px;
            opacity: 0;
            animation: rise 0.5s ease-out 0.1s forwards;
        }

        .candle, .wick {
            position: absolute;
            transition: top 0.08s linear, height 0.08s linear;
        }

        .candle {
            width: ${unsafeCSS(CANDLE_WIDTH)}px;
            border-radius: 2px;
        }

        .wick {
            width: ${unsafeCSS(WICK_WIDTH)}px;
        }

        .caption {
            position: relative;
            font-family: 'Roboto', sans-serif;
            font-size: 13px;
            letter-spacing: 0.08em;
            color: rgba(255, 255, 255, 0.55);
            opacity: 0;
            animation: rise 0.5s ease-out 0.2s forwards;
        }

        @keyframes rise {
            from { opacity: 0; transform: translateY(6px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes breathe {
            0%, 100% { transform: scale(0.92); opacity: 0.7; }
            50% { transform: scale(1.05); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
            .glow, .track, .caption {
                animation: none;
                opacity: 1;
            }

            .candle, .wick {
                transition: none;
            }
        }
    `;

    @state()
    candles: Candle[] = [];

    private timer?: ReturnType<typeof setInterval>;
    private trend = 0;
    private tick = 0;

    constructor() {
        super();

        const seeded: Candle[] = [];
        for (let i = 0; i < CANDLE_COUNT; i++) {
            seeded.push(seedCandle(seeded[seeded.length - 1]));
        }
        this.candles = seeded;
    }

    connectedCallback() {
        super.connectedCallback();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.timer = setInterval(() => this.advance(), UPDATE_INTERVAL);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this.timer);
    }

    /** Fades the splash out before removing it, rather than cutting straight to the app. */
    hide() {
        this.classList.add('is-hidden');
        setTimeout(() => this.remove(), FADE_OUT_MS);
    }

    /** Advances the newest candle, closing it and opening a fresh one every TICKS_PER_CANDLE. */
    private advance() {
        const prev = this.candles;

        if (this.tick + 1 > TICKS_PER_CANDLE) {
            this.tick = 0;
            const shifted = prev.slice(1);
            this.candles = [...shifted, openCandle(shifted[shifted.length - 1])];
        } else {
            this.tick += 1;
            const ticked = tickCandle(prev[prev.length - 1], this.trend);
            this.trend = ticked.trend;
            this.candles = [...prev.slice(0, -1), ticked.candle];
        }
    }

    render() {
        return html`
            <div class="glow"></div>

            <div class="track">
                ${
                    // Keyed by candle id, not position. lit-html's default map reuses parts
                    // by index, so on a shift each element would inherit its neighbour's
                    // geometry and the transition above would animate the swap — making the
                    // whole chart slide sideways instead of stepping over by one candle.
                    repeat(this.candles, candle => candle.id, (candle, index) => {
                        const color = candleColor(candle);
                        const opacity = TRAIL_OPACITY + (1 - TRAIL_OPACITY) * (index / (CANDLE_COUNT - 1));
                        const left = (CANDLE_WIDTH + CANDLE_GAP) * index;

                        return html`
                            <div class="wick" style=${styleMap({
                                left: `${left + (CANDLE_WIDTH - WICK_WIDTH) / 2}px`,
                                top: `${MAX_HEIGHT - candle.high}px`,
                                height: `${Math.max(candle.high - candle.low, 1)}px`,
                                background: color,
                                opacity: `${opacity}`,
                            })}></div>
                            <div class="candle" style=${styleMap({
                                left: `${left}px`,
                                top: `${MAX_HEIGHT - Math.max(candle.open, candle.close)}px`,
                                height: `${Math.max(Math.abs(candle.open - candle.close), 1)}px`,
                                background: color,
                                opacity: `${opacity}`,
                            })}></div>
                        `;
                    })
                }
            </div>

            <div class="caption">LOADING CHARTS...</div>
        `;
    }

}
